// @flow
import { fetchItemsIfNeeded, getItem } from './item';
import { getSaveItemAnalytics, getUnsaveItemAnalytics, trackConversionForAlgolia } from './analytics';
import { LOAD_BIDDER_DATA_SUCCESS, LOG_OUT_BIDDER } from './actions';
import api from '../api/saveItem';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { fetchSavedItemCountsIfNeeded } from './savedItemCount';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import { LOGIN_SUCCESS } from './login';
import { PASSWORD_RESET_SUCCESS } from './passwordReset';

/* Action Types */
export const SAVE_ITEM_FAIL = 'la/user/saveItem/FAIL';
export const SAVE_ITEM_REQUEST = 'la/user/saveItem/REQUEST';
export const SAVE_ITEM_SUCCESS = 'la/user/saveItem/SUCCESS';

export const UNSAVE_ITEM_FAIL = 'la/user/unsaveItem/FAIL';
export const UNSAVE_ITEM_REQUEST = 'la/user/unsaveItem/REQUEST';
export const UNSAVE_ITEM_SUCCESS = 'la/user/unsaveItem/SUCCESS';

// reducer
const DEFAULT_STATE = {};

export type State = {
    ...typeof DEFAULT_STATE,
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;

    switch (action.type) {
        case LOAD_BIDDER_DATA_SUCCESS:
        case LOGIN_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
            existing = {};
            if (action.payload.savedItemsTime) {
                action.payload.savedItemsTime.forEach(item => {
                    existing[item.itemId] = { itemId: Number(item.itemId), saveTs: item.saveTs };
                    existing[item.itemId].status = 'SAVED';
                });
            }
            return existing;

        case LOG_OUT_BIDDER: // reset saved Items to defaults
            return DEFAULT_STATE;

        case SAVE_ITEM_FAIL:
            existing = cloneDeep(state);
            delete existing[action.meta.itemId];
            return existing;

        case UNSAVE_ITEM_REQUEST:
            existing = cloneDeep(state);
            existing[action.payload].status = 'REMOVED';
            return existing;

        case UNSAVE_ITEM_SUCCESS:
            return state;

        case SAVE_ITEM_REQUEST:
            existing = cloneDeep(state);
            existing[action.payload.itemId] = {
                itemId: action.payload.itemId,
                saveTs: action.payload.saveTs,
                status: 'SAVED',
            };
            return existing;

        case SAVE_ITEM_SUCCESS:
            return state;

        case UNSAVE_ITEM_FAIL:
            existing = cloneDeep(state);
            existing[action.meta.itemId].status = 'SAVED';
            return existing;

        default:
            return state;
    }
}

/* SELECTORS */
const saveItemSelector = (state: GlobalState) => state.saveItem;
const idSelector = (state: GlobalState, id) => id;

const savedItemsSelector = createSelector(saveItemSelector, saveItems => saveItems);

export const getSavedItem = createSelector([savedItemsSelector, idSelector], (savedItems, id) => savedItems[id] || {});

export const getSortedSavedItemIdsFromState = createSelector(savedItemsSelector, savedItems => {
    const toBeSorted = Object.keys(savedItems).map(item => savedItems[item]);
    const sorted = toBeSorted.sort((a, b) => b.saveTs - a.saveTs);
    return sorted.map(item => item.itemId);
});

export const isItemSaved = createSelector(getSavedItem, savedItem => savedItem.status === 'SAVED');

/* ACTION CREATORS */
const submitSaveItem = itemId => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        dispatch({
            payload: { itemId, saveTs: Date.now() },
            type: SAVE_ITEM_REQUEST,
        });

        await api.postSaveItem({ authToken, deployment, itemId });
        const promises = [];
        if (isEmpty(getItem(state, itemId))) {
            promises.push(dispatch(fetchItemsIfNeeded([itemId])));
        }
        return Promise.all(promises).then(() => {
            const newState = getState();
            const analytics = getSaveItemAnalytics(newState, itemId);
            dispatch(trackConversionForAlgolia(itemId));
            dispatch({
                meta: analytics,
                payload: itemId,
                type: SAVE_ITEM_SUCCESS,
            });
            dispatch(fetchSavedItemCountsIfNeeded([itemId], true));
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemId },
            payload: error,
            type: SAVE_ITEM_FAIL,
        });
    }
};

const submitUnsaveItem = itemId => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        dispatch({
            payload: itemId,
            type: UNSAVE_ITEM_REQUEST,
        });

        await api.postUnsaveItem({ authToken, deployment, itemId });
        const promises = [];
        if (isEmpty(getItem(state, itemId))) {
            promises.push(dispatch(fetchItemsIfNeeded([itemId], 'saved-item')));
        }
        return Promise.all(promises).then(() => {
            const newState = getState();
            const analytics = getUnsaveItemAnalytics(newState, itemId);
            dispatch({
                meta: analytics,
                payload: itemId,
                type: UNSAVE_ITEM_SUCCESS,
            });
            dispatch(fetchSavedItemCountsIfNeeded([itemId], true));
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemId },
            payload: error,
            type: UNSAVE_ITEM_FAIL,
        });
    }
};

export const toggleItemSave = (itemId: number) => (dispatch: Function, getState: Function) => {
    if (isItemSaved(getState(), itemId)) {
        return dispatch(submitUnsaveItem(itemId));
    } else {
        return dispatch(submitSaveItem(itemId));
    }
};
